import * as React from 'react';
import { setInnerHtml } from 'sos-common/js/utils/helpers';
import { EducationItemList } from 'sos-common/js/components/EducationItemList/EducationItemList';
import { makeTranslator } from 'sos-common/js/utils/translations';
import { shortuid } from 'sos-common/js/utils/guid';
import { SelectBoxOption } from 'sos-common/js/components/FormControls/SelectBox';
import { CheckBox } from 'sos-common/js/components/FormControls/CheckBox';
import { InputError } from 'sos-common/js/components/InputError/InputError';
import { doPost } from 'sos-eservice-common/js/services';
import { ResponseError } from 'sos-common/js/utils/ResponseError';
import BetalningSection from '../PublicForm/FormSections/betalning.section';
import AttentionBox from 'sos-common/js/components/AttentionBox/Attentionbox';
import UtbildningSection from '../PublicForm/FormSections/utbildning.section';
import DokumentSection from '../PublicForm/FormSections/dokument.section';
import PersonuppgifterSection from '../PublicForm/FormSections/personuppgifter.section';
import { FormFieldFile, FormField } from 'sos-common/js/commonTypes/formField.types';
import { FileState } from 'sos-common/js/commonTypes/fileupload.types';
import { ResourceItem } from 'sos-common/js/commonTypes/resourceItem';
import {
  YrkeKostnad,
  SectionClass,
  FieldName,
  SkyddadYrkestitelOpenFormEpiTexts,
  SkyddadYrkestitelOpenFormInfoBubbles,
  FormType,
} from '../PublicForm/PublicForm.types';
import { Yrke } from '../AnsokanForms/LegAnsokanForm.types';
import { EpiserverProperty } from 'sos-common/js/utils/episerver.property';
import {
  DocumentFetcher,
  GetSkyddadYrkestitelDocumentModelParams,
} from '../PublicForm/DocumentModel/document.model.fetcher';
import { DocumentModel } from '../PublicForm/DocumentModel/document.model.types';
import _ from 'lodash';
import { IGoogleReCaptchaConsumerProps } from 'react-google-recaptcha-v3';
import { DialogueError } from 'sos-common/js/components/DialogueError/DialogueError';
import { getDialogueByResponse } from '../../helpers/webform.dialogue.helper';
import { MailType, MailTypeFieldName } from '../../models/email.types';
import LoadingSpinner from 'sos-common/js/components/LoadingSpinner/LoadingSpinner';

export interface ISkyddadYrkestitelPublicFormProps {
  skyddadYrkestitelOpenFormEpiTexts: SkyddadYrkestitelOpenFormEpiTexts;
  isInEditMode: boolean;
  antiForgery?: string;
  clientKey: string;
  translation: ResourceItem[];
  heading: string;
  preamble: string;
  predefinedYrke?: Yrke;
  yrkeKostnader: YrkeKostnad[];
  contentId: number;
  googleReCaptchaProps?: IGoogleReCaptchaConsumerProps | undefined;
  skyddadYrkestitelOpenFormInfoBubbles: SkyddadYrkestitelOpenFormInfoBubbles;
  maxTotalSizeInBytes: number;
  redirectPage: string;
  maxIndividualFileSizeInBytes: number;
  formLanguage: string;
  educatedInSweden: boolean;
  countryList: string[];
  acceptedFileTypes: string[];
}

export interface ISkyddadYrkestitelPublicFormState {
  formData: Map<string, string>;
  activeYrke: Yrke;
  hasErrors: boolean;
  documents: DocumentModel[];
  isErrorDialogOpen: boolean;
  apiLogId?: string;
  samtycker: boolean;
  showSpinner: boolean;
  submitButtonIsClicked?: boolean;
  isFormDirty: boolean;
}

let Translate: (key: string, defaultValue?: string) => string;
const ContentIdFieldName = '__contentId';
const LanguageFIeldName = '__languageId';
const CaptchaFieldName = '__captchaToken';

export class SkyddadYrkestitelPublicForm extends React.Component<
  ISkyddadYrkestitelPublicFormProps,
  ISkyddadYrkestitelPublicFormState
> {
  private _documentFetcher: DocumentFetcher;
  private _documentFetcherParams: GetSkyddadYrkestitelDocumentModelParams;
  private _formRef: React.RefObject<HTMLFormElement>;
  private _errorId: string;
  private _betalningSectionRef: React.RefObject<BetalningSection>;
  private _utbildningSectionRef: React.RefObject<UtbildningSection>;
  private _dokumentSectionRef: React.RefObject<DokumentSection>;
  private _personuppgifterSectionRef: React.RefObject<PersonuppgifterSection>;
  private _sectionRefList: React.RefObject<SectionClass<any, any>>[];

  constructor(props: ISkyddadYrkestitelPublicFormProps) {
    super(props);

    Translate = makeTranslator(props.translation);
    this._formRef = React.createRef();
    this._errorId = shortuid();
    this._sectionRefList = [
      (this._betalningSectionRef = React.createRef()),
      (this._utbildningSectionRef = React.createRef()),
      (this._dokumentSectionRef = React.createRef()),
      (this._personuppgifterSectionRef = React.createRef()),
    ];

    this._documentFetcherParams = {
      yrke: props.predefinedYrke,
      educatedInSweden: props.educatedInSweden,
    };

    this._documentFetcher = new DocumentFetcher(Translate, true, null, props.skyddadYrkestitelOpenFormInfoBubbles);
    let documents = this._documentFetcher.getSkyddadYrkestitelModels(this._documentFetcherParams);

    this.state = {
      formData: new Map<string, string>(),
      activeYrke: props.predefinedYrke || null,
      hasErrors: false,
      documents,
      isErrorDialogOpen: false,
      samtycker: false,
      showSpinner: false,
      isFormDirty: false,
    };
  }

  componentDidMount = () => {
    this.validateAllSections(false);
    window.onbeforeunload = () => {
      if (this.state.isFormDirty) {
        return 'Är du säker på att du vill lämna sidan?';
      }
    };
  };

  componentWillUnmount = () => {
    window.onbeforeunload = null;
  };

  validateAllSections = (setAsDirty: boolean = true) => {
    const { samtycker } = this.state;
    let allFieldsValid = samtycker ? true : false;
    this._sectionRefList.forEach((section) => {
      if (section.current?.hasErrors()) {
        allFieldsValid = false;
      }
    });
    this.setState({
      hasErrors: !allFieldsValid,
      isFormDirty: setAsDirty,
    });
  };

  onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    this.setState({ submitButtonIsClicked: true });
    let formData = new FormData(this._formRef.current as any);

    const removeKeys = [];
    formData.forEach((value, key, parent) => {
      if (key.startsWith('doc_')) {
        removeKeys.push(key);
      }
    });
    removeKeys.forEach((x) => formData.delete(x));

    const documents = _.cloneDeep(this.state.documents);

    documents.forEach((x) => {
      if (x.files == null || x.files.length < 1) {
        formData.append(x.fieldName, null);
      } else {
        x.files.forEach((y) => {
          formData.append(x.fieldName, y.file);
        });
      }
    });

    let partialState = { isErrorDialogOpen: false, apiLogId: null } as ISkyddadYrkestitelPublicFormState;

    if (!this.state.hasErrors) {
      this.setState({ showSpinner: true });
      this.props.googleReCaptchaProps.executeRecaptcha('skyddadyrkestitel_form').then(
        (token) => {
          formData.append(CaptchaFieldName, token);
          formData.append(MailTypeFieldName, MailType.SkyddadYrkestitel.toString());

          doPost<string>('/_api/ansokanform/skicka/', formData)
            .then(
              (res) => {
                if (res instanceof ResponseError) {
                  partialState.isErrorDialogOpen = true;
                  partialState.apiLogId = res.body.errorMessage;
                  partialState.showSpinner = false;
                } else {
                  this.setState({ isFormDirty: false });
                  window.location.href = this.props.redirectPage;
                }
              },
              (rejectReason) => {
                partialState.isErrorDialogOpen = true;
                partialState.showSpinner = false;
              }
            )
            .then((_) => {
              this.setState(partialState);
            });
        },
        (rejection) => {
          console.log(rejection);
        }
      );
    }
  };

  get yrkeSelectBoxOption(): SelectBoxOption[] {
    return this.props.yrkeKostnader.map((yrkeKostnad) => {
      return { key: yrkeKostnad.Yrke, text: Translate(`yrke_${yrkeKostnad.Yrke}`) };
    });
  }

  get yrkesKostnad(): number {
    return this.props.yrkeKostnader.find((x) => x.Yrke === this.state.activeYrke)?.Kostnad;
  }

  getSamtyckeErrorMessage = (): string => {
    if (this.state.submitButtonIsClicked && !this.state.samtycker) {
      return Translate('error_mandatory');
    }
    return '';
  };

  fileInputChanged = (files: FormFieldFile) => {
    const documents = _.cloneDeep(this.state.documents);
    const document = documents.find((doc) => doc.fieldName === files.fieldName);

    if (document) {
      const filesArr = files.value;
      filesArr.forEach((file) => {
        file.status = file.status === FileState.UPLOADING ? FileState.SETTLED : file.status;
        file.FileId = shortuid();
      });
      document.files = filesArr;
    }
    this.setState({ documents });
  };

  onModalClose = () => {
    this.setState({
      isErrorDialogOpen: false,
      apiLogId: null,
    });
  };

  onSamtyckeCheck = (field: FormField<boolean>) => {
    this.setState({ samtycker: field.value, isFormDirty: true }, () => {
      this.validateAllSections();
    });
  };

  public render() {
    const {
      heading,
      preamble,
      isInEditMode,
      skyddadYrkestitelOpenFormEpiTexts,
      contentId,
      skyddadYrkestitelOpenFormInfoBubbles,
      maxTotalSizeInBytes,
      predefinedYrke,
      maxIndividualFileSizeInBytes,
      formLanguage,
      countryList,
      educatedInSweden,
    } = this.props;
    const {
      activeYrke,
      documents,
      isErrorDialogOpen,
      apiLogId,
      samtycker,
      hasErrors,
      showSpinner,
      submitButtonIsClicked,
    } = this.state;

    return (
      <>
        {isErrorDialogOpen && (
          <DialogueError
            dialogueErrorBody={getDialogueByResponse(apiLogId)}
            isModalOpen={isErrorDialogOpen}
            onModalClose={this.onModalClose}
            enableCloseIcon={true}
            shouldCloseOnOverlayClick={false}
          />
        )}

        <EpiserverProperty propertyName="Heading" isInEditMode={isInEditMode}>
          <h1>{heading}</h1>
        </EpiserverProperty>

        <EpiserverProperty propertyName="Preamble" wrappingElement="span" isInEditMode={isInEditMode}>
          <p className="preamble">{preamble}</p>
        </EpiserverProperty>

        <AttentionBox
          bodytext={Translate('varning_lamnasidan')}
          cssClass="attentionbox--brown m-b-2 m-t-2"
          icon="Infocirkel"
        />

        <hr className="formdivider" />

        <form ref={this._formRef} noValidate method="post" id="leg-public-form" onSubmit={this.onSubmit}>
          <input type="hidden" name={ContentIdFieldName} value={contentId} />
          <input type="hidden" name={LanguageFIeldName} value={formLanguage} />
          {educatedInSweden && <input type="hidden" name={FieldName.AngeLand} value={Translate('sweden')} />}

          {predefinedYrke && <input type="hidden" name={FieldName.Yrke} value={predefinedYrke} />}

          {(isInEditMode || activeYrke) && (
            <>
              <BetalningSection
                activeYrke={activeYrke}
                ref={this._betalningSectionRef}
                isInEditMode={isInEditMode}
                skyddadYrkestitelOpenFormEpiTexts={skyddadYrkestitelOpenFormEpiTexts}
                onChangeFieldValue={this.validateAllSections}
                Translate={Translate}
                kostnad={this.yrkesKostnad}
                submitButtonIsClicked={this.state.submitButtonIsClicked}
                formType={FormType.SkyddadYrkestitel}
              />

              <UtbildningSection
                ref={this._utbildningSectionRef}
                isInEditMode={isInEditMode}
                skyddadYrkestitelOpenFormEpiTexts={skyddadYrkestitelOpenFormEpiTexts}
                skyddadYrkestitelOpenFormInfoBubbles={skyddadYrkestitelOpenFormInfoBubbles}
                onChangeFieldValue={this.validateAllSections}
                Translate={Translate}
                formLanguage={formLanguage}
                submitButtonIsClicked={this.state.submitButtonIsClicked}
                formType={FormType.SkyddadYrkestitel}
                countryList={countryList}
                educatedInSweden={educatedInSweden}
              />

              <DokumentSection
                ref={this._dokumentSectionRef}
                isInEditMode={isInEditMode}
                skyddadYrkestitelOpenFormEpiTexts={skyddadYrkestitelOpenFormEpiTexts}
                documents={documents}
                onChangeFieldValue={this.validateAllSections}
                fileInputChanged={this.fileInputChanged}
                Translate={Translate}
                maxTotalSizeInBytes={maxTotalSizeInBytes}
                maxIndividualFileSizeInBytes={maxIndividualFileSizeInBytes}
                submitButtonIsClicked={this.state.submitButtonIsClicked}
                formType={FormType.SkyddadYrkestitel}
                maxFileuploadErrorMessage={Translate('fileupload_error_maxfiles' || undefined)}
                hideOriginalDocumentsCheckbox={true}
                acceptedFileTypes={this.props.acceptedFileTypes}
              />

              <PersonuppgifterSection
                ref={this._personuppgifterSectionRef}
                isInEditMode={isInEditMode}
                skyddadYrkestitelOpenFormEpiTexts={skyddadYrkestitelOpenFormEpiTexts}
                onChangeFieldValue={this.validateAllSections}
                Translate={Translate}
                formLanguage={formLanguage}
                submitButtonIsClicked={this.state.submitButtonIsClicked}
                formType={FormType.SkyddadYrkestitel}
              />

              {isInEditMode && (
                <div className="form-group-xs-small flex">
                  <input className="m-r-1" type="checkbox" />
                  <EpiserverProperty propertyName="SamtyckeLabel" isInEditMode={isInEditMode}>
                    <div className="like-a-label">{skyddadYrkestitelOpenFormEpiTexts.SamtyckeLabel}</div>
                  </EpiserverProperty>
                </div>
              )}
              {!isInEditMode && (
                <CheckBox
                  fieldName={FieldName.Samtycke}
                  stringValue={Translate('ja')}
                  errorMessage={this.getSamtyckeErrorMessage()}
                  onChange={this.onSamtyckeCheck}
                  label={skyddadYrkestitelOpenFormEpiTexts.SamtyckeLabel + ' ' + Translate('mandatory')}
                />
              )}

              <EpiserverProperty
                propertyName="ReadmorePersonuppgifter"
                usingDangerouslySetInnerHTML={true}
                isInEditMode={isInEditMode}>
                <div
                  dangerouslySetInnerHTML={setInnerHtml(skyddadYrkestitelOpenFormEpiTexts.ReadmorePersonuppgifter)}
                />
              </EpiserverProperty>

              <div className="m-b-4 m-t-2">
                <div className="form-submit">
                  <button className="button relative" type="submit" disabled={showSpinner}>
                    {Translate('skickain')}
                    {showSpinner && <LoadingSpinner cssClasses="button-spinner" />}
                  </button>
                  {submitButtonIsClicked && hasErrors && (
                    <InputError id={this._errorId} errorText={Translate('error_missing_fields')} />
                  )}
                </div>
              </div>
            </>
          )}
        </form>
      </>
    );
  }
}
